import React, { useEffect, useRef, useState } from 'react';
import usePlaceholder from './usePlaceholder';
import { twMerge } from 'tailwind-merge';

import { RadioButton, Input, TopikList, useT } from '@repo/common';

import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-top.svg';

// value 는 상태 비교·저장에 쓰이므로 그대로 두고, label 만 렌더 시점에 번역한다
const TOPIK_OPTIONS = [
  { value: 'not required', label: ['Not required', '불필요'] },
  { value: 'preferred', label: ['Preferred', '우대'] },
  { value: 'required', label: ['Required', '필수'] },
  {
    value: 'required with minimum level',
    label: ['Required with minimum level', '필수 (최소 등급 지정)'],
  },
];

const TopikRadioGroup = ({
  label,
  value,
  level,
  onChange,
  onLevelChange,
  disabled = false,
  className,
}) => {
  const t = useT();
  const ph = usePlaceholder();
  const topikRef = useRef(null);

  const [isTopikOpen, setIsTopikOpen] = useState(false);
  const selectedTopik = level || 'Select level';

  useEffect(() => {
    if (!value) {
      onChange?.('not required');
    }
  }, [onChange, value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (topikRef.current && !topikRef.current.contains(e.target)) {
        setIsTopikOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={twMerge('w-full flex flex-col gap-2', className)}>
      <div className='flex items-center gap-0.5'>
        <span className='typo-b12Strong tabletH:typo-b16 text-text-primary'>
          {label}
        </span>
      </div>

      <div className='w-full bg-grayscale-gray00 tabletH:rounded-lg tabletH:border tabletH:border-grayscale-gray02 tabletH:p-2'>
        <div className='grid grid-cols-1 gap-y-2 tabletH:grid-cols-2'>
          {TOPIK_OPTIONS.map((option) => {
            const isMinimum = option.value === 'required with minimum level';
            const isSelected = value === option.value;
            const optionLabel = t(option.label[0], option.label[1]);

            return (
              <React.Fragment key={option.value}>
                <div className='flex h-fit w-full min-w-0 items-start gap-2 py-2 tabletH:py-3'>
                  <RadioButton
                    checked={isSelected}
                    onChange={() => onChange?.(option.value)}
                    disabled={disabled}
                    ariaLabel={optionLabel}
                  />

                  <div className='flex w-full min-w-0 flex-col items-start gap-2'>
                    <span
                      className={twMerge(
                        'typo-b16',
                        disabled
                          ? 'text-text-placeholder'
                          : 'text-text-primary',
                      )}
                    >
                      {optionLabel}
                    </span>
                    {isMinimum && (
                      <div
                        ref={topikRef}
                        className='relative flex flex-col w-full gap-2'
                      >
                        <Input
                          placeholder={ph(
                            t('Select level', '등급을 선택하세요'),
                          )}
                          value={
                            selectedTopik === 'Select level'
                              ? ''
                              : selectedTopik
                          }
                          label=''
                          className='w-full'
                          rightIcon={
                            isTopikOpen ? (
                              <ArrowTopIcon className='text-icon-placeholder' />
                            ) : (
                              <ArrowBottomIcon className='text-icon-placeholder' />
                            )
                          }
                          readOnly
                          onClick={() => setIsTopikOpen((prev) => !prev)}
                          onRightIconClick={() =>
                            setIsTopikOpen((prev) => !prev)
                          }
                        />
                        {isTopikOpen && (
                          <div className='absolute left-0 top-[50px] z-20 w-full'>
                            <TopikList
                              fullWidth
                              selectedOption={selectedTopik}
                              onSelect={(option) => {
                                onLevelChange?.(option);
                                setIsTopikOpen(false);
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TopikRadioGroup;
